import React, { useEffect, useState } from 'react';
import { isNumber } from 'lodash';
import { Center, Stack } from '@fiverr-private/layout_components';
import { NumberedList, ListItem } from '@fiverr-private/list';
import { Button } from '@fiverr-private/button';
import { Video } from '@fiverr-private/media';
import { ExternalLinkIcon } from '@fiverr-private/visuals';
import { getContext } from '@fiverr-private/fiverr_context';
import { ElementType } from '@fiverr-private/bianka_js';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { AppI18n } from '../../i18n';
import { useStepValidation } from '../../hooks/use-step-validation/useStepValidation';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { GifWrapper } from '../../components/gif-wrapper/GifWrapper';
import { StepTitle } from '../../components/step-title/StepTitle';
import { useStoreData } from '../../hooks/useStoreData';
import { GIF_URL, SHOPIFY_URL } from './SignUp.constants';

const LIST_ITEMS = [
    {
        id: 1,
        text: <AppI18n k="steps.sign_up.list.item_1" templates={{ strong: (text) => <strong>{text}</strong> }} />,
    },
    {
        id: 2,
        text: <AppI18n k="steps.sign_up.list.item_2" templates={{ strong: (text) => <strong>{text}</strong> }} />,
    },
    {
        id: 3,
        text: <AppI18n k="steps.sign_up.list.item_3" templates={{ strong: (text) => <strong>{text}</strong> }} />,
    },
];

export const SignUp: React.FC = () => {
    const { pageCtxId, userId } = getContext();
    const { currentPath } = useWizardNavigation();
    const { registerStepValidation, unregisterStepValidation } = useStepValidation();
    const [hasClickedSignUp, setHasClickedSignUp] = useState(false);
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();
    const { storeData } = useStoreData();

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    useEffect(() => {
        const validationStatusFn = () => (hasClickedSignUp ? 'valid' : 'invalid');

        registerStepValidation('sign-up', validationStatusFn);

        return () => {
            unregisterStepValidation('sign-up');
        };
    }, [hasClickedSignUp, registerStepValidation, unregisterStepValidation]);

    const handleSignUpClick = () => {
        setHasClickedSignUp(true);
        reportClickEvent({
            biEventData: {
                userId,
                ctxId: pageCtxId,
            },
            eventType: 'clicked_on_sign_up_to_shopify',
            elementType: ElementType.LINK,
            currentPath,
        });

        const shopifyUrl = new URL(SHOPIFY_URL);

        if (isNumber(userId)) {
            shopifyUrl.searchParams.append('sharedid', userId.toString());
        }

        if (storeData?.id) {
            shopifyUrl.searchParams.append('subid3', storeData.id);
        }

        window.open(shopifyUrl.href, '_blank');
    };

    return (
        <Center>
            <Stack direction="column" gap="2" color="grey_1200" ref={pageImpressionRef}>
                <StepTitle title={<AppI18n k="steps.sign_up.title" />} />
                <Stack direction="column" gap="8">
                    <NumberedList rowGap="4" paddingLeft="4">
                        {LIST_ITEMS.map(({ id, text }) => (
                            <ListItem key={id} marginBottom="2">
                                {text}
                            </ListItem>
                        ))}
                    </NumberedList>
                    <GifWrapper>
                        <Video
                            sources={[
                                {
                                    type: 'video/mp4',
                                    src: GIF_URL,
                                },
                            ]}
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls={false}
                            width={{ default: 310, sm: 488, md: 514 }}
                        />
                    </GifWrapper>
                    <Button onClick={handleSignUpClick} alignSelf="flexStart" variant="outline">
                        <AppI18n k="steps.sign_up.cta" /> <ExternalLinkIcon />
                    </Button>
                </Stack>
            </Stack>
        </Center>
    );
};
